/* eslint-disable */
import { connect } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { SelectPicker } from 'rsuite';
import { BaseModal } from '../BaseModal';
import { formActions, modalActions } from '../../../Actions';
import { Label } from '@/Components/ui/label';
import { Input } from '@/Components/ui/input';
import wastageAnalyticsServices from '@/Services/Analytics Services/wastage-analytics.services';
import { notificationServices } from '@/Services';
import { Button } from '@/Components/ui/button';
import { PRODUCT_CLASSES } from '@/Constants/product.constants';

const initialState = {
	reason: '',
	storeType: null,
	storeSubType: null,
	categoryId: null,
	skuType: null,
};

const AddWastageReasonModal = (props) => {
	const {
		show,
		data,
		toggleModal,
		staticEnums,
		productCategoriesList,
		fetchProductCategory,
	} = props;
	const isEdit = data?.isEdit === true;
	const { heading = (isEdit ? 'Edit Wastage Reason' : 'Add Wastage Reason') } = data ?? {};

	const [state, setState] = useState({
		...initialState,
	});
	const [loading, setLoading] = useState(false);

	// Lazy-fetch the master category list when the modal opens, only if it
	// isn't already in Redux.
	useEffect(() => {
		if (!show) return;
		if (Array.isArray(productCategoriesList) && productCategoriesList.length > 0) return;
		fetchProductCategory({
			entity: 'PRODUCT_CATEGORY',
			req: {
				params: { id: '' },
			},
			method: 'LIST',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show]);

	// Pre-fill the form when the modal opens in edit mode. Resolve the row's
	// categoryId from the various shapes the backend might send it in.
	useEffect(() => {
		if (!show) return;
		if (isEdit && data?.record) {
			const r = data.record;
			const cid = r?.categoryId
				?? r?.productCategoryId
				?? r?.category?.id
				?? null;
			setState({
				reason: r?.reason ?? '',
				storeType: r?.storeType ?? null,
				storeSubType: r?.storeSubType ?? null,
				categoryId: cid,
				skuType: r?.skuType ?? null,
			});
		} else {
			setState({ ...initialState });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, isEdit, data?.record?.id]);

	const storeTypeOptions = useMemo(() => {
		const enumKeys = Object.keys(staticEnums?.StoreTypeEnum ?? {});
		return enumKeys.map((ele) => ({ label: ele, value: ele }));
	}, [staticEnums?.StoreTypeEnum]);

	const storeSubTypeOptions = useMemo(() => {
		const enumKeys = Object.keys(staticEnums?.StoreSubTypeEnum ?? {});
		return enumKeys.map((ele) => ({ label: ele, value: ele }));
	}, [staticEnums?.StoreSubTypeEnum]);

	const categoryOptions = useMemo(() => {
		return (productCategoriesList ?? []).map((cat) => ({
			label: cat?.productCategoryName ?? '—',
			value: cat?.id,
		}));
	}, [productCategoriesList]);

	// SKU Type options come from the central PRODUCT_CLASSES list
	// (Constants/product.constants.js). Backend expects uppercase ids
	// (PRODUCT / INGREDIENT / PACKAGING / …).
	const skuTypeOptions = useMemo(() => {
		return (PRODUCT_CLASSES ?? []).map((cls) => ({
			label: cls?.name,
			value: cls?.id?.toUpperCase?.() ?? cls?.id,
		}));
	}, []);

	const handleClose = () => {
		toggleModal(false, {});
		setState(initialState);
	};

	const handleSubmit = async () => {
		if (!state?.reason?.trim()) {
			notificationServices.generateNotification({
				message: 'Wastage Reason is Mandatory',
				type: 'error',
			});
			return;
		}
		if (!state?.storeType && !state?.storeSubType) {
			notificationServices.generateNotification({
				message: 'Store Type or Store Sub Type any one is Mandatory',
				type: 'error',
			});
			return;
		}
		setLoading(true);
		try {
			const response = await wastageAnalyticsServices.addWastageReason({
				req: {
					data: {
						...state,
						reason: state?.reason?.trim(),
						// In edit mode, include the existing record's id so the
						// backend updates instead of creating a new entry.
						...(isEdit && data?.record?.id != null && { id: data.record.id }),
					},
				},
			});
			if (response?.statusCode === 200) {
				handleClose();
				if (data?.onSuccessCallback) {
					data?.onSuccessCallback();
				}
				notificationServices.generateNotification({
					message: response?.message,
					type: 'success',
				});
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error', error);
		} finally {
			setLoading(false);
		}
	};

	const renderBtns = () => {
		// Category and SKU Type are optional. Only Reason + (Store Type OR
		// Store Sub Type) are mandatory.
		const isDisable = !state.reason?.trim() || (!state.storeType && !state.storeSubType);

		return (
			<div className='w-full flex gap-2'>
				<Button
					onClick={handleClose}
					variant='secondary'
					className='flex-1'
					disabled={loading}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={isDisable || loading}
					className='bg-uy-color hover:bg-[#f1f1f1] hover:text-black flex-1 text-black'
				>
					{loading ? (
						<div className='flex items-center gap-2'>
							<div className='animate-spin rounded-full h-4 w-4 border-b-2 border-black'></div>
							{isEdit ? 'Updating...' : 'Creating...'}
						</div>
					) : (
						isEdit ? 'Update' : 'Create'
					)}
				</Button>
			</div>
		);
	};

	return (
		<BaseModal
			open={show}
			heading={heading}
			handleClose={handleClose}
			width={data?.width ?? '36rem'}
			height={data?.height ?? 'fit-content'}
			renderBtns={renderBtns}
		>
			<div className='flex flex-col gap-4 px-4 py-2'>
				<div className='flex flex-col items-start gap-1'>
					<Label className='text-red-500'>Wastage Reason <span className='text-red-500'>*</span></Label>
					<Input
						className='w-full'
						placeholder='Enter Wastage Reason'
						value={state?.reason ?? ''}
						onChange={(event) => {
							setState((prev) => ({
								...prev,
								reason: event?.target?.value,
							}));
						}}
					/>
				</div>

				<div className='flex flex-col items-start gap-1 w-full'>
					<div className='flex items-center gap-2'>
						<Label>Store Type</Label>
						<span className='text-[11.5px] text-red-500 font-medium'>(Selecting either Store Type or Store Sub Type is mandatory)</span>
					</div>
					<SelectPicker
						data={storeTypeOptions}
						value={state?.storeType}
						onChange={(value) => {
							setState((prev) => ({
								...prev,
								storeType: value,
							}));
						}}
						placeholder='Select Store Type'
						disabled={Boolean(state?.storeSubType)}
						cleanable
						searchable
						block
						style={{ width: '100%' }}
						menuStyle={{ zIndex: 9999 }}
					/>
				</div>

				<div className='flex flex-col items-start gap-1 w-full'>
					<Label>Store Sub Type</Label>
					<SelectPicker
						data={storeSubTypeOptions}
						value={state?.storeSubType}
						onChange={(value) => {
							setState((prev) => ({
								...prev,
								storeSubType: value,
							}));
						}}
						placeholder='Select Store Sub Type'
						disabled={Boolean(state?.storeType)}
						cleanable
						searchable
						block
						style={{ width: '100%' }}
						menuStyle={{ zIndex: 9999 }}
					/>
				</div>

				<div className='flex flex-col items-start gap-1 w-full'>
					<Label>Select Category </Label>
					<SelectPicker
						data={categoryOptions}
						value={state?.categoryId}
						onChange={(value) => {
							setState((prev) => ({
								...prev,
								categoryId: value,
							}));
						}}
						placeholder='Select Category'
						cleanable
						searchable
						block
						placement='top'
						style={{ width: '100%' }}
						menuStyle={{ zIndex: 9999 }}
					/>
				</div>

				<div className='flex flex-col items-start gap-1 w-full'>
					<Label>SKU Type</Label>
					<SelectPicker
						data={skuTypeOptions}
						value={state?.skuType}
						onChange={(value) => {
							setState((prev) => ({
								...prev,
								skuType: value,
							}));
						}}
						placeholder='Select SKU Type'
						cleanable
						searchable
						placement='top'
						block
						style={{ width: '100%' }}
						menuStyle={{ zIndex: 9999 }}
					/>
				</div>
			</div>
		</BaseModal>
	);
};

const mapStateToProps = (state) => {
	const { show, data } = state.modal.addWastageReasonModal;
	const { staticEnums } = state.auth;
	const productCategoriesList = state?.form?.PRODUCT_CATEGORY?.data?.LIST ?? [];
	return {
		show,
		data,
		staticEnums,
		productCategoriesList,
	};
};

const mapDispatchToProps = {
	toggleModal: modalActions.toggleAddWastageReasonModal,
	fetchProductCategory: formActions.gateway,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(AddWastageReasonModal);
